import { createSlice, current } from '@reduxjs/toolkit'
import { formatCreditCardNumber } from 'utils/helpers'

const createEmptyCard = () => ({
  attributes: {
    id: '',
    first_name: '',
    last_name: '',
    card_type: '',
    card_number: '',
    expiration_date: '',
    date_entered: '',
    date_modified: '',
    bank_card_type: '',
    verified: false,
    used_card_refund: false,
    existing_card: '',
    existing_card_check: false,
    contact_id: '',
    luhn_check: '',
    bin_check: '',
    credit_card_token: '',
    is_manual: '0',
    ocr_status: '0',
    is_digital_wallet: '0',
    wallet_provider: '',
    redaction_verified: '0',
    redaction_message: '',
    encrypted_filename: '',
  },
  ocr: {
    card_number: { ocr: 0, score: '', manual: 0 },
    expiration_date: { ocr: 0, score: '', manual: 0 },
    first_name: { ocr: 0, score: '', manual: 0 },
    last_name: { ocr: 0, score: '', manual: 0 }
  },
  verify: {
    first_name: false,
    last_name: false,
    expiration_date: false,
    bank_card_type: false,
    purchase_card_number: false,
    card_number: false
  },
  files: [],
  valid: {
    first_name: '',
    last_name: '',
    card_number: '',
    expiration_date: '',
    bank_card_type: ''
  },
  invalid: {
    first_name: '',
    last_name: '',
    card_number: '',
    expiration_date: '',
    bank_card_type: ''
  },
  validation_messages: {
    first_name: '',
    last_name: '',
    card_number: '',
    expiration_date: '',
    bank_card_type: ''
  }
})

// Refund flags arrive as booleans from the UI and as '1'/'0' from the API
export const isRefundFlagOn = value =>
  value === true || value === '1' || value === 1

const isVerifyFlagOn = value => value === true || value === '1' || value === 1

// A refund selection is required for the section, so it gates every card
export const isPaymentCardFullyVerified = (card, anyRefundSelected) => {
  const verify = card?.verify || {}
  return (
    isVerifyFlagOn(verify.first_name) &&
    isVerifyFlagOn(verify.last_name) &&
    (isVerifyFlagOn(verify.purchase_card_number) ||
      isVerifyFlagOn(verify.card_number)) &&
    isVerifyFlagOn(verify.expiration_date) &&
    isVerifyFlagOn(verify.bank_card_type) &&
    anyRefundSelected
  )
}

export const isPaymentCardVerified = card =>
  isVerifyFlagOn(card?.attributes?.verified)

const hasAnyRefundSelected = paymentCards =>
  (paymentCards || []).some(card =>
    isRefundFlagOn(card?.attributes?.used_card_refund)
  )

const withVerified = (card, anyRefundSelected) => ({
  ...card,
  attributes: {
    ...card.attributes,
    verified: isPaymentCardFullyVerified(card, anyRefundSelected)
  }
})

export const applyRefundSelection = (paymentCards, cardIndex, refund) => {
  const cards = paymentCards || []
  if (!cards[cardIndex]?.attributes) return cards

  const hadRefundSelected = hasAnyRefundSelected(cards)
  const nextRefund = isRefundFlagOn(refund)

  const withRefund = cards.map((card, idx) => {
    if (!card?.attributes) return card
    if (idx === cardIndex) {
      return {
        ...card,
        attributes: { ...card.attributes, used_card_refund: nextRefund }
      }
    }
    if (nextRefund && isRefundFlagOn(card.attributes.used_card_refund)) {
      return {
        ...card,
        attributes: { ...card.attributes, used_card_refund: false }
      }
    }
    return card
  })

  const nextRefundSelected = hasAnyRefundSelected(withRefund)
  const refundGateChanged = nextRefundSelected !== hadRefundSelected

  return withRefund.map((card, idx) => {
    if (!card?.attributes) return card
    if (!refundGateChanged && idx !== cardIndex) return card
    return withVerified(card, nextRefundSelected)
  })
}

const initialState = {
  // Credit cards (legacy support)
  creditCards: [createEmptyCard()],

  // Payment cards (purchase cards)
  paymentCards: [createEmptyCard()]
}

export const paymentCardsSlice = createSlice({
  name: 'paymentCards',
  initialState,
  reducers: {
    // Initialize
    initializePaymentCards: state => {
      state.paymentCards = [createEmptyCard()]
    },
    initializeCreditCards: state => {
      state.creditCards = [createEmptyCard()]
    },

    // Credit Cards (legacy)
    setCreditCard: (state, action) => {
      const { card_no, attributes } = action.payload
      if (attributes.card_number) {
        attributes.card_number = formatCreditCardNumber(attributes.card_number)
      }
      state.creditCards[card_no].attributes = {
        ...state.creditCards[card_no].attributes,
        ...attributes
      }
    },
    setCreditCardOcr: (state, action) => {
      const { card_no, ocr } = action.payload
      state.creditCards[card_no].ocr = {
        ...state.creditCards[card_no].ocr,
        ...ocr
      }
    },
    setCreditCardVerify: (state, action) => {
      const { card_no, verify } = action.payload
      state.creditCards[card_no].verify = {
        ...state.creditCards[card_no].verify,
        ...verify
      }
    },

    // Payment Cards (purchase cards)
    setPaymentCard: (state, action) => {
      const { no, attributes } = action.payload
      if (attributes.card_number) {
        attributes.card_number = formatCreditCardNumber(attributes.card_number)
      }
      state.paymentCards[no].attributes = {
        ...state.paymentCards[no].attributes,
        ...attributes
      }
    },
    setPaymentCardOcr: (state, action) => {
      const { no, ocr } = action.payload
      state.paymentCards[no].ocr = { ...state.paymentCards[no].ocr, ...ocr }
    },
    updatePaymentCardOcrField: (state, action) => {
      const { no, field, data } = action.payload
      state.paymentCards[no].ocr[field] = {
        ...state.paymentCards[no].ocr[field],
        ...data
      }
    },
    setPaymentCardVerify: (state, action) => {
      const { no, verify } = action.payload
      state.paymentCards[no].verify = {
        ...state.paymentCards[no].verify,
        ...verify
      }
    },

    // Card management
    addPaymentCard: state => {
      state.paymentCards.push(createEmptyCard())
    },
    addCreditCard: state => {
      state.creditCards.push(createEmptyCard())
    },
    removePaymentCard: (state, action) => {
      const { no } = action.payload
      state.paymentCards.splice(no, 1)
    },
    removeCreditCard: (state, action) => {
      const { no } = action.payload
      state.creditCards.splice(no, 1)
    },

    // File management for payment cards
    addPaymentCardFile: (state, action) => {
      const { no } = action.payload
      state.paymentCards[no].files.push({})
    },
    setPaymentCardFile: (state, action) => {
      const { no, file_no, file_obj, base64 } = action.payload
      state.paymentCards[no].files[file_no] = { file_obj, base64 }
    },
    setPaymentCardFileID: (state, action) => {
      const { no, file_no, id, document_revision_id } = action.payload
      if (state.paymentCards[no].files[file_no]) {
        state.paymentCards[no].files[file_no].id = id
        if (document_revision_id) {
          state.paymentCards[no].files[file_no].file_obj.document_revision_id =
            document_revision_id
        }
      }
    },
    addPaymentCardFileWithData: (state, action) => {
      const { no, file } = action.payload
      if (state.paymentCards[no].files.length < 3) {
        state.paymentCards[no].files.push(file)
      }
    },
    removePaymentCardFile: (state, action) => {
      const { no, file_no } = action.payload
      state.paymentCards[no].files.splice(file_no, 1)
    },
    setAllPaymentCardFiles: (state, action) => {
      const { no, files } = action.payload
      state.paymentCards[no].files = files
    },

    // File management for credit cards
    addCreditCardFile: (state, action) => {
      const { card_no } = action.payload
      state.creditCards[card_no].files.push({})
    },
    setCreditCardFile: (state, action) => {
      const { card_no, file_no, file_obj, base64 } = action.payload
      state.creditCards[card_no].files[file_no] = { file_obj, base64 }
    },
    setCreditCardFileID: (state, action) => {
      const { card_no, file_no, id, document_revision_id } = action.payload
      if (state.creditCards[card_no].files[file_no]) {
        state.creditCards[card_no].files[file_no].id = id
        if (document_revision_id) {
          state.creditCards[card_no].files[
            file_no
          ].file_obj.document_revision_id = document_revision_id
        }
      }
    },
    addCreditCardFileWithData: (state, action) => {
      const { card_no, file } = action.payload
      state.creditCards[card_no].files.push(file)
    },
    removeCreditCardFile: (state, action) => {
      const { card_no, file_no } = action.payload
      state.creditCards[card_no].files.splice(file_no, 1)
    },
    setAllCreditCardFiles: (state, action) => {
      const { card_no, files } = action.payload
      state.creditCards[card_no].files = files
    },

    // Validation
    setPaymentCardValidity: (state, action) => {
      const { no, field_name, valid, invalid, message } = action.payload
      state.paymentCards[no].valid[field_name] = valid
      state.paymentCards[no].invalid[field_name] = invalid
      state.paymentCards[no].validation_messages[field_name] = message
    },

    // Bulk verification
    verifyAllPaymentCards: (state, action) => {
      const { value } = action.payload
      state.paymentCards.forEach(card => {
        Object.keys(card.verify).forEach(key => {
          card.verify[key] = value
        })
      })
    },

    // Set payment cards from API
    setPaymentCardsFromAPI: (state, action) => {
      const paymentCardsData = action.payload

      if (Array.isArray(paymentCardsData)) {
        const transformedCards = paymentCardsData.map((card, index) => {
          // Preserve existing validation messages if they exist
          const existingCard = state.paymentCards[index]
          const existingValidationMessages =
            existingCard?.validation_messages || {}

          return {
            attributes: {
              id: card.attributes?.id || '',
              first_name: card.attributes?.first_name || '',
              last_name: card.attributes?.last_name || '',
              card_type: card.attributes?.card_type || '',
              card_number: card.attributes?.card_number || '',
              expiration_date: card.attributes?.expiration_date || '',
              bank_card_type: card.attributes?.bank_card_type || '',
              verified: card.attributes?.verified || false,
              used_card_refund: card.attributes?.used_card_refund || false,
              date_entered: card.attributes?.date_entered || '',
              date_modified: card.attributes?.date_modified || '',
              existing_card: '',
              existing_card_check: false,
              contact_id: card.attributes?.contact_id || '',
              luhn_check: card.attributes?.luhn_check || '',
              bin_check: card.attributes?.bin_check || '',
              credit_card_token: card.attributes?.credit_card_token || '',
              is_manual: card.attributes?.is_manual || '0',
              ocr_status: card.attributes?.ocr_status || '0',
              is_digital_wallet: card.attributes?.is_digital_wallet || '0',
              wallet_provider: card.attributes?.wallet_provider || '',
              redaction_verified: card.attributes?.redaction_verified || '0',
              redaction_message: card.attributes?.redaction_message || '',
              encrypted_filename: card.attributes?.encrypted_filename || ''
            },
            ocr: card.ocr || {},
            verify: {
              first_name: card.verify?.first_name || false,
              last_name: card.verify?.last_name || false,
              purchase_card_number: card.verify?.purchase_card_number || false,
              card_number: card.verify?.card_number || false,
              expiration_date: card.verify?.expiration_date || false,
              bank_card_type: card.verify?.bank_card_type || false
            },
            files: card.files || [],
            valid: {
              first_name: '',
              last_name: '',
              card_number: '',
              expiration_date: '',
              bank_card_type: ''
            },
            invalid: {
              first_name: '',
              last_name: '',
              card_number: '',
              expiration_date: '',
              bank_card_type: ''
            },
            validation_messages: {
              first_name: existingValidationMessages.first_name || '',
              last_name: existingValidationMessages.last_name || '',
              card_number: existingValidationMessages.card_number || '',
              expiration_date: existingValidationMessages.expiration_date || '',
              bank_card_type: existingValidationMessages.bank_card_type || ''
            },
            fieldChanges: {},
            needsAutoSave: false,
            lastModified: null
          }
        })
        // Direct assignment to ensure state update
        state.paymentCards =
          transformedCards.length > 0 ? transformedCards : [createEmptyCard()]
      } else {
        // If data is not an array, set to empty card
        state.paymentCards = [createEmptyCard()]
      }
    },

    // Update individual payment card field
    updatePaymentCard: (state, action) => {
      const { cardIndex, field, value } = action.payload
      if (state.paymentCards[cardIndex]) {
        // Handle nested properties like verify.first_name
        if (field.includes('.')) {
          const [parentKey, childKey] = field.split('.')
          if (state.paymentCards[cardIndex][parentKey]) {
            state.paymentCards[cardIndex][parentKey][childKey] = value
          }
        } else {
          // Handle direct attributes
          state.paymentCards[cardIndex].attributes[field] = value
        }
      }
    },

    // Update validation messages
    updatePaymentCardValidation: (state, action) => {
      const { cardIndex, field, message } = action.payload
      if (state.paymentCards[cardIndex]) {
        state.paymentCards[cardIndex].validation_messages[field] = message
      }
    },

    // Add new card Id
    setNewCardId: (state, action) => {
      const { cardId, index } = action.payload
      state.paymentCards[index] = {
        ...state.paymentCards[index],
        attributes: {
          ...state.paymentCards[index].attributes,
          id: cardId
        }
      }
    },

    // Set payment card refund
    setPaymentCardRefund: (state, action) => {
      const { cardIndex, refund } = action.payload
      if (!state.paymentCards[cardIndex]?.attributes) return

      state.paymentCards = applyRefundSelection(
        current(state.paymentCards),
        cardIndex,
        refund
      )
    },

    // Set bank card type
    setPaymentCardBankCardType: (state, action) => {
      const { cardIndex, bankCardType } = action.payload
      state.paymentCards[cardIndex].attributes.bank_card_type = bankCardType
    },

    // Set verified
    setPaymentCardVerified: (state, action) => {
      const { cardIndex, verified } = action.payload
      state.paymentCards[cardIndex].attributes.verified = verified
    },

    // Set payment card existing card
    setPaymentCardExistingCard: (state, action) => {
      const { cardIndex, existingCard } = action.payload
      state.paymentCards[cardIndex].attributes.existing_card = existingCard
    },

    // Set payment card existing card check
    setPaymentCardExistingCardCheck: (state, action) => {
      const { cardIndex, existingCardCheck } = action.payload
      state.paymentCards[cardIndex].attributes.existing_card_check =
        existingCardCheck
    },

    // set feild value
    setPaymentCardFieldValue: (state, action) => {
      const { cardIndex, field, value } = action.payload
      if (state.paymentCards[cardIndex]) {
        state.paymentCards[cardIndex].attributes[field] = value
      }
    },
    setPaymentCardsFromAPICustomerModal: (state, action) => {
      const paymentCardsData = action.payload
      if (paymentCardsData) {
        // Update the first payment card with the new data
        if (state.paymentCards.length > 0) {
          state.paymentCards[0].attributes = {
            ...state.paymentCards[0].attributes,
            ...paymentCardsData
          }
        } else {
          // If no payment cards exist, create one with the data
          state.paymentCards = [
            {
              ...createEmptyCard(),
              attributes: {
                ...createEmptyCard().attributes,
                ...paymentCardsData
              }
            }
          ]
        }
      }
    },
    setPaymentCardLuhnCheck: (state, action) => {
      const { cardIndex, luhnCheck } = action.payload
      state.paymentCards[cardIndex].attributes.luhn_check = luhnCheck
    },
    setPaymentCardBinCheck: (state, action) => {
      const { cardIndex, binCheck } = action.payload
      state.paymentCards[cardIndex].attributes.bin_check = binCheck
    },
    resetPaymentCardFields: (state, action) => {
      const { no } = action.payload
      const existing = state.paymentCards[no]
      if (!existing) return
      const empty = createEmptyCard()
      state.paymentCards[no] = {
        ...empty,
        attributes: {
          ...empty.attributes,
          id: existing.attributes?.id || '',
          contact_id: existing.attributes?.contact_id || ''
        },
        files: existing.files || []
      }
    },
    markPaymentCardFilesAsNewRelation: (state, action) => {
      const { no } = action.payload
      const card = state.paymentCards[no]
      if (!card || !Array.isArray(card.files)) return
      card.files = card.files.map(f => {
        if (!f) return f
        const next = { ...f, new_relation_addition: true }
        if (f.file_obj) {
          next.file_obj = { ...f.file_obj, new_relation_addition: true }
        }
        return next
      })
    }
  }
})

export const {
  initializePaymentCards,
  initializeCreditCards,
  setCreditCard,
  setCreditCardOcr,
  setCreditCardVerify,
  setPaymentCard,
  setPaymentCardOcr,
  updatePaymentCardOcrField,
  setPaymentCardVerify,
  addPaymentCard,
  addCreditCard,
  removePaymentCard,
  removeCreditCard,
  addPaymentCardFile,
  setPaymentCardFile,
  setPaymentCardFileID,
  addPaymentCardFileWithData,
  removePaymentCardFile,
  setAllPaymentCardFiles,
  addCreditCardFile,
  setCreditCardFile,
  setCreditCardFileID,
  addCreditCardFileWithData,
  removeCreditCardFile,
  setAllCreditCardFiles,
  setPaymentCardValidity,
  verifyAllPaymentCards,
  setPaymentCardsFromAPI,
  updatePaymentCard,
  updatePaymentCardValidation,
  setNewCardId,
  setPaymentCardRefund,
  setPaymentCardBankCardType,
  setPaymentCardVerified,
  setPaymentCardExistingCard,
  setPaymentCardExistingCardCheck,
  setPaymentCardFieldValue,
  setCardFieldChanged,
  markCardForAutoSave,
  clearCardAutoSave,
  setPaymentCardsFromAPICustomerModal,
  setPaymentCardLuhnCheck,
  setPaymentCardBinCheck,
  resetPaymentCardFields,
  markPaymentCardFilesAsNewRelation
} = paymentCardsSlice.actions

// Selectors
export const selectPaymentCards = state => state.paymentCards.paymentCards
export const selectCreditCards = state => state.paymentCards.creditCards
export const selectPaymentCard = (state, index) =>
  state.paymentCards.paymentCards[index]
export const selectCreditCard = (state, index) =>
  state.paymentCards.creditCards[index]
export const selectPaymentCardFiles = (state, index) =>
  state.paymentCards.paymentCards[index]?.files || []
export const selectCreditCardFiles = (state, index) =>
  state.paymentCards.creditCards[index]?.files || []

export default paymentCardsSlice.reducer
